import {
  InfoMarkdown,
  OutputProcessMessage,
  RegExpPattern,
  areConfigsEqual,
  fileAudio,
  fileExcel,
  fileImage,
  filePdf,
  filePowerPoint,
  fileVideo,
  fileWord,
  fileZip,
  getFileExtension,
  getFileType,
  humanFileSize,
  markdownUtil
} from '@b3networks/shared/common';
import { format } from 'date-fns';
import { ConvoType, MsgType, SystemMsgType } from '../../enums.model';
import { AttachmentMessageData, AttachmentMessageDataV2, StatusMessage } from '../chat-message-attachment.model';
import { PreviewMessageData } from '../chat-message-data.model';
import { IMessBodyData } from '../chat-message-imess.model';
import { InteractiveMessageData } from '../chat-message-interactive.model';
import { SystemMessageData } from '../chat-message-system.model';
import { ChatMessage, MessageBody } from '../chat-message.model';
import { AttachmentFileType, ChatMessageAttachmentUI, PreviewMessageUI } from '../chat-messsage-ui.model';

export const SYSTEM_TYPE_MSG_CUSTOM_RENDER = [
  SystemMsgType.pin,
  SystemMsgType.unpin,
  SystemMsgType.transferOwner,
  SystemMsgType.threadCreate
];

export const MSG_TYPE_BUILDER = {
  [MsgType.message]: (m: ChatMessage) => {
    if ([ConvoType.NEWSFEED].includes(m.ct)) {
      if (!m.body?.preview && !m.body?.title) {
        m.state.isErrorFormat = true;
        m.body = new MessageBody({
          ...m.body,
          preview: 'Unrenderable post!'
        });
        m.buildMsg.builtTextMessage = buildMarkdownMessage(
          m,
          <InfoMarkdown>{
            hyperId: m.hs,
            noCode: true,
            noSingleCode: true
          },
          m.buildMsg?.builtTextMessage
        );
        return;
      }
    } else {
      if (!m.body?.text) {
        m.state.isErrorFormat = true;
        m.body = new MessageBody({
          ...m.body,
          text: 'Unrenderable message!'
        });
        m.buildMsg.builtTextMessage = buildMarkdownMessage(
          m,
          <InfoMarkdown>{
            hyperId: m.hs,
            noCode: true,
            noSingleCode: true
          },
          m.buildMsg?.builtTextMessage
        );
        return;
      }
    }
    initPreviewMessage(m);
    // build markdown
    m.buildMsg.builtTextMessage = buildMarkdownMessage(
      m,
      {
        hyperId: m.hs,
        noCode: m.newInstanceFrom?.reply,
        noSingleCode: m.newInstanceFrom?.reply
      },
      m.buildMsg?.builtTextMessage
    );
  },
  [MsgType.system]: (m: ChatMessage) => {
    const system = m.body?.data as SystemMessageData;
    m.state.isCustomRender = SYSTEM_TYPE_MSG_CUSTOM_RENDER.includes(system?.type);
    if (!m.state.isCustomRender) {
      if (m.isStore) {
        const txt = system && !!system.text ? system.text : m.body?.text;
        if (!txt) {
          m.state.isErrorFormat = true;
          m.body = new MessageBody({
            ...m.body,
            text: 'Unrenderable message!'
          });
          m.buildMsg.builtTextMessage = buildMarkdownMessage(
            m,
            <InfoMarkdown>{
              hyperId: m.hs,
              noCode: true,
              noSingleCode: true
            },
            m.buildMsg?.builtTextMessage
          );
          return;
        }
      }

      // build markdown
      m.buildMsg.builtTextMessage = buildMarkdownMessage(
        m,
        {
          hyperId: m.hs,
          noCode: m.newInstanceFrom?.reply,
          noSingleCode: m.newInstanceFrom?.reply
        },
        m.buildMsg?.builtTextMessage
      );
    }
  },
  [MsgType.attachment]: (m: ChatMessage) => {
    if (!m.body?.text) {
      m.body = new MessageBody({
        ...m.body,
        text: '[Attachment]'
      });
    }

    m.attachmentUI.attachmentData = (<AttachmentMessageDataV2>m.body.data)?.attachment || m.body.data;
    buildAttachment(m);

    // build markdown
    m.buildMsg.builtTextMessage = buildMarkdownAttachment(m);

    // build markdown caption
    if (m.attachmentUI.attachmentData.caption) {
      m.buildMsg.buildTextCaption = buildMarkdownCaption(
        m.attachmentUI.attachmentData.caption,
        {
          noCode: true
        },
        m.buildMsg?.buildTextCaption
      );
    }
  },
  [MsgType.sticker]: (m: ChatMessage) => {
    if (!m.body?.text) {
      m.body = new MessageBody({
        ...m.body,
        text: '[Sticker]'
      });
    }
    m.attachmentUI.attachmentData = (<AttachmentMessageDataV2>m.body.data)?.attachment || m.body.data;
    buildAttachment(m);

    // build markdown
    m.buildMsg.builtTextMessage = buildMarkdownAttachment(m);

    // build markdown caption
    if (m.attachmentUI.attachmentData.caption) {
      m.buildMsg.buildTextCaption = buildMarkdownCaption(
        m.attachmentUI.attachmentData.caption,
        {
          noCode: true,
          noSingleCode: true
        },
        m.buildMsg?.buildTextCaption
      );
    }
  },
  [MsgType.imess]: (m: ChatMessage) => {
    if (m.state.isInteractiveV2) {
      // interactive
      m.state.isErrorFormat =
        !m.body?.data ||
        typeof m.body?.data === 'string' ||
        !(<IMessBodyData>m.body?.data)?.components ||
        (<IMessBodyData>m.body?.data)?.components.length === 0;
      if (m.state.isErrorFormat) {
        m.body = new MessageBody({
          ...m?.body,
          text: m.body?.text
            ? m.body.text
            : m?.buildMsg?.newByLastMessageChannel
              ? 'No preview available'
              : 'Unrenderable message!'
        });
        m.buildMsg.builtTextMessage = buildMarkdownMessage(
          m,
          {
            hyperId: m.hs,
            noCode: true,
            noSingleCode: true
          },
          m.buildMsg?.builtTextMessage
        );
        return;
      }
    } else {
      // webhook
      m.state.isErrorFormat =
        !m.body?.data || typeof m.body?.data === 'string' || !(<InteractiveMessageData>m.body?.data)?.blocks;
      if (m.state.isErrorFormat) {
        m.body = new MessageBody({
          ...m?.body,
          text: m.body?.text
            ? m.body.text
            : m?.buildMsg?.newByLastMessageChannel
              ? 'No preview available'
              : 'Unrenderable message!'
        });
        m.buildMsg.builtTextMessage = buildMarkdownMessage(
          m,
          {
            hyperId: m.hs,
            noCode: true,
            noSingleCode: true
          },
          m.buildMsg?.builtTextMessage
        );
        return;
      }
      m.webhookUI.interactiveData = m.body.data;
      m.webhookUI.renderHeaderMarkdown = markdownUtil(m.body?.text);
    }
  },
  [MsgType.mcq]: (m: ChatMessage) => {
    if (!m.body?.text) {
      m.state.isErrorFormat = true;
      m.body = new MessageBody({
        ...m.body,
        text: 'Unrenderable message!'
      });
      m.buildMsg.builtTextMessage = buildMarkdownMessage(
        m,
        {
          hyperId: m.hs,
          noCode: true,
          noSingleCode: true
        },
        m.buildMsg?.builtTextMessage
      );
      return;
    }
    // build markdown
    const config = <InfoMarkdown>{
      hyperId: m.hs,
      noCode: m.newInstanceFrom?.reply,
      noSingleCode: m.newInstanceFrom?.reply
    };
    m.buildMsg.builtTextMessage = buildMarkdownMessage(m, config, m.buildMsg?.builtTextMessage);
  }
};

export function buildMarkdownAttachment(m: ChatMessage) {
  // body.title is fileName , should not have markdown
  const textMessage = m.body.title || m.body.text;
  return <OutputProcessMessage>{
    text: textMessage,
    isTriggerDirective: false
  };
}

export function buildMarkdownMessage(m: ChatMessage, config: InfoMarkdown, oldBuild: OutputProcessMessage) {
  let textMessage = m.body.text;
  const data = m.body?.data as SystemMessageData;
  if (!!data?.text && [MsgType.system, MsgType.imess, MsgType.webhook].includes(m.mt)) {
    textMessage = data.text;
  }

  // check cache
  if (!!oldBuild && !!oldBuild.originText && oldBuild.originText === textMessage) {
    if (areConfigsEqual(oldBuild.oldConfig, config)) {
      return <OutputProcessMessage>{ ...oldBuild };
    }
  }

  const transferHTML = markdownUtil(textMessage, config);
  // add edited
  if (!m.attachmentUI?.isFailed && m.aclActions?.isEditedMessage) {
    const isUrl = m?.body?.text && m.body.text.match(RegExpPattern.URL)?.length > 0;
    if (!isUrl && transferHTML.text) {
      const date = format(m?.metadata?.editedAt, "MMM d 'at' HH:mm:ss");
      transferHTML.text += `<small class="tooltip b3n-text-secondary edited" style='line-height: initial'> (edited)<span class="tooltiptext"> ${date} </span></small>`;
    }
  }
  return transferHTML;
}

export function buildMarkdownCaption(caption: string, config: InfoMarkdown, oldBuild: OutputProcessMessage) {
  // check cache
  if (!!oldBuild && !!oldBuild.originText && oldBuild.originText === caption) {
    return <OutputProcessMessage>{ ...oldBuild };
  }
  const transferHTML = markdownUtil(caption, config);
  return transferHTML;
}

function initPreviewMessage(m: ChatMessage) {
  const isTeamchat = [ConvoType.direct, ConvoType.groupchat, ConvoType.THREAD].includes(m.ct);
  const data = <PreviewMessageData>m.body?.data;
  m.state.isPreviewMsg =
    isTeamchat && !!m.metadata?.editedAt && Boolean(data?.desc || data?.icon || data?.image || data?.title);
  if (m.state.isPreviewMsg) {
    const obj = <PreviewMessageUI>{
      ...m.previewMessageUI
    };
    obj.previewData = m.body.data as PreviewMessageData;
    obj.backgroundImage =
      obj.previewData.image && obj.previewData?.image?.indexOf('https') >= 0 ? `url('${obj.previewData.image}')` : '';
    const match = m.body.text?.match(RegExpPattern.YOUTUBE);
    obj.isYoutube = !!m.body?.text && !!match;
    const links = m.body.text?.match(RegExpPattern.URL);
    obj.extractUrl = links && links.length > 0 ? links[0] : '#';
    if (!obj.isYoutube) {
      if (obj.previewData.desc) {
        const div = document.createElement('div');
        div.innerHTML =
          obj.previewData.desc?.length > 200 ? obj.previewData.desc?.slice(0, 200) + '...' : obj.previewData.desc;
        obj.htmlDecode = div.childNodes.length === 0 ? '' : div.childNodes[0].nodeValue || '';
      }
    } else {
      obj.embededUrlYoutube = match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1` : '';
      obj.srcDocYoutube = match
        ? `<style>*{padding:0;margin:0;overflow:hidden}html,body{height:100%}img,span{position:absolute;width:100%;top:0;bottom:0;margin:auto}span{height:1.5em;text-align:center;font:48px/1.5 sans-serif;color:white;text-shadow:0 0 0.5em black}</style><a href=https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1><img src=https://img.youtube.com/vi/${match[1]}/mqdefault.jpg><span>▶</span></a>`
        : '';
    }

    m.previewMessageUI = <PreviewMessageUI>{
      ...m.previewMessageUI,
      ...obj
    };
  }
}

function buildAttachment(m: ChatMessage) {
  if (m.attachmentUI.attachmentData) {
    let obj = <ChatMessageAttachmentUI>{ ...m.attachmentUI };

    const attachmentData = <AttachmentMessageData>m.attachmentUI.attachmentData;
    const size = humanFileSize(attachmentData.size);
    const fileType = getFileTypeMsg(attachmentData);
    const isMediaId = !!attachmentData?.mediaId;
    const isStorage = !!attachmentData?.uri && attachmentData.uri.startsWith('storage://');
    obj = <ChatMessageAttachmentUI>{
      ...obj,
      fileType,
      type: getAttachmentType(fileType),
      logoFileType: getFileType(fileType),
      sizeFile: size === 'NaN undefined' ? null : size,
      keyWithOrgUuid: isMediaId
        ? attachmentData.mediaId
        : isStorage
          ? attachmentData.uri.replace('storage://', '')
          : attachmentData.mediaUuid || attachmentData.fileUuid,
      isMediaId,
      isStorage,
      isFailed: (<AttachmentMessageDataV2>m.body.data)?.waSentStatus?.status === StatusMessage.failed,
      logo: buildLogo(fileType)
    };
    m.attachmentUI = {
      ...m.attachmentUI,
      ...obj
    };
  }
}

function getAttachmentType(fileType: string) {
  if (fileVideo.indexOf(fileType) > -1) {
    return AttachmentFileType.VIDEO;
  } else if (fileAudio.indexOf(fileType) > -1) {
    return AttachmentFileType.AUDIO;
  } else if (fileImage.indexOf(fileType) > -1) {
    return AttachmentFileType.IMAGE;
  } else {
    return AttachmentFileType.FILE;
  }
}

function getFileTypeMsg(data: AttachmentMessageData) {
  let fileType;
  if (data.fileType) fileType = data.fileType?.toLowerCase().split('/')?.pop();
  if (!fileType) fileType = getFileExtension(data?.name);
  return fileType;
}

function buildLogo(fileType: string): { isIcon: boolean; icon: string; isSvg?: boolean } {
  if (fileType) {
    fileType = fileType.toLowerCase();

    if (fileVideo.indexOf(fileType) >= 0) {
      return {
        isIcon: true,
        icon: 'videocam',
        isSvg: false
      };
    } else if (fileAudio.indexOf(fileType) >= 0) {
      return {
        isIcon: true,
        icon: 'music_note',
        isSvg: false
      };
    } else if (fileWord.indexOf(fileType) >= 0) {
      return {
        isIcon: false,
        icon: 'assets/icons/attachment/word.png'
      };
    } else if (fileExcel.indexOf(fileType) >= 0) {
      return {
        isIcon: false,
        icon: 'assets/icons/attachment/excel.png'
      };
    } else if (filePowerPoint.indexOf(fileType) >= 0) {
      return {
        isIcon: false,
        icon: 'assets/icons/attachment/power_point.png'
      };
    } else if (fileZip.indexOf(fileType) >= 0) {
      return {
        isIcon: true,
        icon: 'folder_zip',
        isSvg: false
      };
    } else if (filePdf.indexOf(fileType) >= 0) {
      return {
        isIcon: true,
        icon: 'picture_as_pdf',
        isSvg: false
      };
    } else {
      return {
        isIcon: true,
        icon: 'attach_file',
        isSvg: false
      };
    }
  } else {
    return {
      isIcon: true,
      icon: 'attach_file',
      isSvg: false
    };
  }
}
